/**
 * Single fetch wrapper used by all API hooks.
 * - Same-origin in dev (Vite proxy → FastAPI) and in prod (nginx → backend).
 * - Cookies are sent automatically (auth lives in HttpOnly cookies).
 */
export class ApiError extends Error {
  status: number
  data: unknown
  constructor(status: number, message: string, data: unknown) {
    super(message)
    this.status = status
    this.data = data
  }
}

type Options = Omit<RequestInit, "body"> & { body?: unknown }

const BASE = (import.meta.env.VITE_API_BASE_URL as string | undefined) ?? "/api"

export async function api<T>(path: string, opts: Options = {}): Promise<T> {
  const { body, headers, ...rest } = opts
  const isForm = body instanceof FormData
  const init: RequestInit = {
    credentials: "include",
    ...rest,
    headers: {
      ...(isForm ? {} : { "Content-Type": "application/json" }),
      ...(headers ?? {}),
    },
    body: body === undefined ? undefined : isForm ? body : JSON.stringify(body),
  }
  const res = await fetch(`${BASE}${path}`, init)
  const contentType = res.headers.get("content-type") ?? ""
  const payload = contentType.includes("application/json")
    ? await res.json()
    : await res.text()
  if (!res.ok) {
    const message =
      typeof payload === "object" && payload && "detail" in payload
        ? String((payload as { detail: unknown }).detail)
        : `Hata (${res.status})`
    throw new ApiError(res.status, message, payload)
  }
  return payload as T
}
